import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import ClubCard from '../components/ClubCard';
import { getFollowingClubs } from '../services/api';
import { Users, ArrowLeft, UserCheck } from 'lucide-react';
import { DotLoader } from 'react-spinners';
import defaultAvatar from '../assets/default-avatar.jpg'
import Footer from '../components/Footer';

function FollowingClubs() {
  const navigate = useNavigate();
  const [clubsData, setClubsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFollowingClubs();
  }, []);

  const fetchFollowingClubs = async () => {
    try {
      const data = await getFollowingClubs();
      setClubsData(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/clubs-list')}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-6 transition"
        >
          <ArrowLeft size={20} className="mr-2" />
          Retour
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
            <Users className="mr-3 text-indigo-500" size={36} />
            Clubs de mes amis
          </h1>
          <p className="text-gray-400">
            Découvrez les clubs auxquels participent les personnes que vous suivez
          </p>
        </div>

        {clubsData.length === 0 ? (
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-12 text-center">
            <Users size={64} className="mx-auto text-slate-700 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Aucun club trouvé
            </h2>
            <p className="text-gray-400 mb-6">
              Les personnes que vous suivez ne participent à aucun club pour le moment, ou vous ne suivez personne encore.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition font-semibold shadow-lg shadow-indigo-500/20"
            >
              Explorer les clubs
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {clubsData.map((clubData) => (
              <div key={clubData.club._id} className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg overflow-hidden">
                {/* Section du club */}
                <div className="p-6">
                  <ClubCard club={clubData.club} hideJoin={false}/>
                </div>

                {/* Section des amis dans ce club */}
                <div className="bg-slate-800 border-t border-slate-700 px-6 py-4">
                  <div className="flex items-center mb-3">
                    <UserCheck size={20} className="text-indigo-500 mr-2" />
                    <h3 className="font-semibold text-white">
                      {clubData.followingMembers.length} de vos ami{clubData.followingMembers.length > 1 ? 's' : ''} dans ce club
                    </h3>
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    {clubData.followingMembers.map((member) => (
                      <div
                        key={member._id}
                        onClick={() => navigate(`/user/${member._id}`)}
                        className="flex items-center bg-slate-950 border border-slate-700 hover:border-indigo-500 px-4 py-2 rounded-full shadow-sm hover:shadow-md transition cursor-pointer group"
                      >
                        <img
                          src={member.photoProfil || defaultAvatar}
                          alt={`${member.prenom} ${member.nom}`}
                          className="w-8 h-8 rounded-full object-cover border-2 border-indigo-500 mr-2"
                          onError={(e) => {
                            e.target.src = defaultAvatar;
                          }}
                        />
                        <span className="text-sm font-medium text-white group-hover:text-indigo-400">
                          {member.prenom} {member.nom}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bouton pour voir le club */}
                <div className="px-6 py-4 bg-slate-950 border-t border-slate-800">
                  <button
                    onClick={() => navigate(`/club/${clubData.club._id}`)}
                    className="w-full py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition font-semibold shadow-lg shadow-indigo-500/20"
                  >
                    Voir le club
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {clubsData.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-lg shadow p-6 text-center hover:border-indigo-500 transition">
              <p className="text-3xl font-bold text-indigo-500">{clubsData.length}</p>
              <p className="text-gray-400 mt-1">Clubs avec vos amis</p>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-lg shadow p-6 text-center hover:border-indigo-500 transition">
              <p className="text-3xl font-bold text-indigo-500">
                {clubsData.reduce((sum, cd) => sum + cd.followingMembers.length, 0)}
              </p>
              <p className="text-gray-400 mt-1">Total d'amis dans ces clubs</p>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default FollowingClubs;